import axios from "../config/axios";
import config from "../config/index";

/**
 * Centralized Trust Hub API service
 * Consolidates all compliance-related APIs for Dashboard, Business Profile,
 * Phone Inventory, Voice Compliance, and SMS Compliance
 */
const trustHubCentralized = {
  // ============================================
  // Dashboard APIs
  // ============================================

  /**
   * Get comprehensive dashboard status
   * @param {string} userId - User ID
   * @returns {Promise} Dashboard data including profile, voice, SMS, and phone inventory status
   */
  getDashboardStatus: (userId) =>
    axios
      .get(`${config.apiBaseURL2}trust-hub/dashboard/${userId}`)
      .then((resp) => resp.data)
      .catch((err) => {
        console.error('Dashboard API Error:', err);
        return {
          success: false,
          message: err.response?.data?.message || 'Failed to load dashboard',
          error: err.message
        };
      }),

  // ============================================
  // Business Profile APIs
  // ============================================

  /**
   * Get Trust Hub profile
   * @param {string} userId - User ID
   * @returns {Promise} Profile data
   */
  getProfile: (userId) =>
    axios
      .get(`${config.apiBaseURL2}trust-hub/profile/get/${userId}`)
      .then((resp) => resp.data)
      .catch((err) => {
        console.error('Get Profile Error:', err);
        return {
          success: false,
          message: err.response?.data?.message || 'Failed to load profile',
          error: err.message
        };
      }),

  /**
   * Create Trust Hub profile
   * @param {Object} profileData - Profile form data
   * @returns {Promise} Created profile data
   */
  createProfile: (profileData) =>
    axios
      .post(`${config.apiBaseURL2}trust-hub/profile/create`, profileData)
      .then((resp) => resp.data)
      .catch((err) => {
        console.error('Create Profile Error:', err);
        return {
          success: false,
          message: err.response?.data?.message || 'Failed to create profile',
          error: err.message
        };
      }),

  /**
   * Update Trust Hub profile
   * @param {Object} profileData - Profile form data
   * @returns {Promise} Updated profile data
   */
  updateProfile: (profileData) =>
    axios
      .post(`${config.apiBaseURL2}trust-hub/profile/update`, profileData)
      .then((resp) => resp.data)
      .catch((err) => {
        console.error('Update Profile Error:', err);
        return {
          success: false,
          message: err.response?.data?.message || 'Failed to update profile',
          error: err.message
        };
      }),

  /**
   * Get profile status
   * @param {string} userId - User ID
   * @returns {Promise} Profile status
   */
  getProfileStatus: (userId) =>
    axios
      .get(`${config.apiBaseURL2}trust-hub/profile/status/${userId}`)
      .then((resp) => resp.data)
      .catch((err) => {
        console.error('Get Profile Status Error:', err);
        return {
          success: false,
          message: err.response?.data?.message || 'Failed to load profile status',
          error: err.message
        };
      }),

  /**
   * Get profile usage across channels
   * @param {string} userId - User ID
   * @returns {Promise} Usage data including voice campaigns, SMS campaigns, phone assignments
   */
  getProfileUsage: (userId) =>
    axios
      .get(`${config.apiBaseURL2}trust-hub/profile/usage/${userId}`)
      .then((resp) => resp.data)
      .catch((err) => {
        console.error('Get Profile Usage Error:', err);
        return {
          success: false,
          message: err.response?.data?.message || 'Failed to load profile usage',
          data: {
            voice_campaigns: 0,
            sms_campaigns: 0,
            phone_assignments: 0,
            total_usage: 0
          }
        };
      }),

  /**
   * Check Twilio status
   * @param {string} userId - User ID
   * @returns {Promise} Twilio status
   */
  checkTwilioStatus: (userId) =>
    axios
      .get(`${config.apiBaseURL2}trust-hub/profile/twilio-status/${userId}`)
      .then((resp) => resp.data)
      .catch((err) => {
        console.error('Check Twilio Status Error:', err);
        return {
          success: false,
          message: err.response?.data?.message || 'Failed to check Twilio status',
          error: err.message
        };
      }),

  // ============================================
  // Voice Compliance APIs
  // ============================================

  /**
   * Get CNAM trust product
   * @param {string} userId - User ID
   * @returns {Promise} CNAM product data
   */
  getCNAM: (userId) =>
    axios
      .get(`${config.apiBaseURL2}trust-hub/profile/get-cnam-trust-product/${userId}`)
      .then((resp) => resp.data)
      .catch((err) => {
        console.error('Get CNAM Error:', err);
        return {
          success: false,
          message: err.response?.data?.message || 'Failed to load CNAM',
          error: err.message
        };
      }),

  /**
   * Create CNAM trust product
   * @param {Object} cnamData - CNAM configuration data
   * @returns {Promise} Created CNAM product
   */
  createCNAM: (cnamData) =>
    axios
      .post(`${config.apiBaseURL2}trust-hub/profile/create-cnam-trust-product`, cnamData)
      .then((resp) => resp.data)
      .catch((err) => {
        console.error('Create CNAM Error:', err);
        return {
          success: false,
          message: err.response?.data?.message || 'Failed to create CNAM',
          error: err.message
        };
      }),

  /**
   * Update CNAM trust product
   * @param {Object} cnamData - CNAM configuration data
   * @returns {Promise} Updated CNAM product
   */
  updateCNAM: (cnamData) =>
    axios
      .post(`${config.apiBaseURL2}trust-hub/profile/update-cnam-trust-product`, cnamData)
      .then((resp) => resp.data)
      .catch((err) => {
        console.error('Update CNAM Error:', err);
        return {
          success: false,
          message: err.response?.data?.message || 'Failed to update CNAM',
          error: err.message
        };
      }),

  /**
   * Get STIR/SHAKEN trust product
   * @param {string} userId - User ID
   * @returns {Promise} STIR/SHAKEN product data
   */
  getStirShaken: (userId) =>
    axios
      .get(`${config.apiBaseURL2}trust-hub/profile/get-stir-shaken-trust-product/${userId}`)
      .then((resp) => resp.data)
      .catch((err) => {
        console.error('Get STIR/SHAKEN Error:', err);
        return {
          success: false,
          message: err.response?.data?.message || 'Failed to load STIR/SHAKEN',
          error: err.message
        };
      }),

  /**
   * Create SHAKEN/STIR trust product
   * @param {Object} stirData - SHAKEN/STIR configuration data
   * @returns {Promise} Created SHAKEN/STIR product
   */
  createShakenStir: (stirData) =>
    axios
      .post(`${config.apiBaseURL2}trust-hub/profile/create-stir-shaken-trust-product`, stirData)
      .then((resp) => resp.data)
      .catch((err) => {
        console.error('Create SHAKEN/STIR Error:', err);
        return {
          success: false,
          message: err.response?.data?.message || 'Failed to create SHAKEN/STIR',
          error: err.message
        };
      }),

  /**
   * Update SHAKEN/STIR trust product
   * @param {Object} stirData - SHAKEN/STIR configuration data
   * @returns {Promise} Updated SHAKEN/STIR product
   */
  updateShakenStir: (stirData) =>
    axios
      .post(`${config.apiBaseURL2}trust-hub/profile/update-stir-shaken-trust-product`, stirData)
      .then((resp) => resp.data)
      .catch((err) => {
        console.error('Update SHAKEN/STIR Error:', err);
        return {
          success: false,
          message: err.response?.data?.message || 'Failed to update SHAKEN/STIR',
          error: err.message
        };
      }),

  /**
   * Get Voice Integrity trust product
   * @param {string} userId - User ID
   * @returns {Promise} Voice Integrity product data
   */
  getVoiceIntegrity: (userId) =>
    axios
      .get(`${config.apiBaseURL2}trust-hub/profile/get-voice-integrity-trust-product/${userId}`)
      .then((resp) => resp.data)
      .catch((err) => {
        console.error('Get Voice Integrity Error:', err);
        return {
          success: false,
          message: err.response?.data?.message || 'Failed to load Voice Integrity',
          error: err.message
        };
      }),

  /**
   * Create Voice Integrity trust product
   * @param {Object} integrityData - Voice Integrity configuration data
   * @returns {Promise} Created Voice Integrity product
   */
  createVoiceIntegrity: (integrityData) =>
    axios
      .post(`${config.apiBaseURL2}trust-hub/profile/create-voice-integrity-trust-product`, integrityData)
      .then((resp) => resp.data)
      .catch((err) => {
        console.error('Create Voice Integrity Error:', err);
        return {
          success: false,
          message: err.response?.data?.message || 'Failed to create Voice Integrity',
          error: err.message
        };
      }),

  /**
   * Update Voice Integrity trust product
   * @param {Object} integrityData - Voice Integrity configuration data
   * @returns {Promise} Updated Voice Integrity product
   */
  updateVoiceIntegrity: (integrityData) =>
    axios
      .post(`${config.apiBaseURL2}trust-hub/profile/update-voice-integrity-trust-product`, integrityData)
      .then((resp) => resp.data)
      .catch((err) => {
        console.error('Update Voice Integrity Error:', err);
        return {
          success: false,
          message: err.response?.data?.message || 'Failed to update Voice Integrity',
          error: err.message
        };
      }),

  /**
   * Get trust product status
   * @param {string} userId - User ID
   * @returns {Promise} Trust product status
   */
  getTrustProductStatus: (userId) =>
    axios
      .get(`${config.apiBaseURL2}trust-hub/profile/get-trust-product-status/${userId}`)
      .then((resp) => resp.data)
      .catch((err) => {
        console.error('Get Trust Product Status Error:', err);
        return {
          success: false,
          message: err.response?.data?.message || 'Failed to load trust product status',
          error: err.message
        };
      }),

  // ============================================
  // SMS Compliance (A2P 10DLC) APIs
  // ============================================

  /**
   * Get A2P status (brand + campaign)
   * @param {string} userId - User ID
   * @returns {Promise} A2P status data
   */
  getA2PStatus: (userId) =>
    axios
      .get(`${config.apiBaseURL2}a2p/status/${userId}`)
      .then((resp) => resp.data)
      .catch((err) => {
        console.error('Get A2P Status Error:', err);
        return {
          success: false,
          message: err.response?.data?.message || 'Failed to load A2P status',
          error: err.message
        };
      }),

  /**
   * Get A2P brand details
   * @param {string} userId - User ID
   * @returns {Promise} Brand details
   */
  getBrandDetails: (userId) =>
    axios
      .get(`${config.apiBaseURL2}a2p/brand/${userId}/details`)
      .then((resp) => resp.data)
      .catch((err) => {
        console.error('Get Brand Details Error:', err);
        return {
          success: false,
          message: err.response?.data?.message || 'Failed to load brand details',
          error: err.message
        };
      }),

  /**
   * Create A2P brand
   * @param {Object} brandData - Brand registration data
   * @returns {Promise} Created brand data
   */
  createBrand: (brandData) =>
    axios
      .post(`${config.apiBaseURL2}a2p/brand/create`, brandData)
      .then((resp) => resp.data)
      .catch((err) => {
        console.error('Create Brand Error:', err);
        return {
          success: false,
          message: err.response?.data?.message || 'Failed to create brand',
          error: err.message
        };
      }),

  /**
   * Update A2P brand
   * @param {Object} brandData - Brand registration data
   * @returns {Promise} Updated brand data
   */
  updateBrand: (brandData) =>
    axios
      .post(`${config.apiBaseURL2}a2p/brand/update`, brandData)
      .then((resp) => resp.data)
      .catch((err) => {
        console.error('Update Brand Error:', err);
        return {
          success: false,
          message: err.response?.data?.message || 'Failed to update brand',
          error: err.message
        };
      }),

  /**
   * Get A2P campaign details
   * @param {string} userId - User ID
   * @returns {Promise} Campaign details
   */
  getCampaignDetails: (userId) =>
    axios
      .get(`${config.apiBaseURL2}a2p/campaign/${userId}/details`)
      .then((resp) => resp.data)
      .catch((err) => {
        console.error('Get Campaign Details Error:', err);
        return {
          success: false,
          message: err.response?.data?.message || 'Failed to load campaign details',
          error: err.message
        };
      }),

  /**
   * Create A2P campaign
   * @param {Object} campaignData - Campaign registration data
   * @returns {Promise} Created campaign data
   */
  createCampaign: (campaignData) =>
    axios
      .post(`${config.apiBaseURL2}a2p/campaign/create`, campaignData)
      .then((resp) => resp.data)
      .catch((err) => {
        console.error('Create Campaign Error:', err);
        return {
          success: false,
          message: err.response?.data?.message || 'Failed to create campaign',
          error: err.message
        };
      }),

  /**
   * Update A2P campaign
   * @param {Object} campaignData - Campaign registration data
   * @returns {Promise} Updated campaign data
   */
  updateCampaign: (campaignData) =>
    axios
      .post(`${config.apiBaseURL2}a2p/campaign/update`, campaignData)
      .then((resp) => resp.data)
      .catch((err) => {
        console.error('Update Campaign Error:', err);
        return {
          success: false,
          message: err.response?.data?.message || 'Failed to update campaign',
          error: err.message
        };
      }),

  // ============================================
  // Phone Inventory APIs
  // ============================================

  /**
   * Get user phone numbers from zyra_twilio_numbers table
   *
   * DATABASE: zyra_twilio_numbers table
   * FILTER BY: user_id column
   *
   * RESPONSE FIELD MAPPING:
   * - twilio_phone: Phone number from database twilio_phone column
   * - number_type: Type of number (outbound/inbound/sms/voip) from database number_type column
   * - status: Assignment status (assigned/available) - calculated based on assignment_type
   * - id: Unique identifier from database id column
   *
   * EXPECTED RESPONSE FORMAT:
   * {
   *   success: true,
   *   data: {
   *     data: [
   *       {
   *         id: number,                    // Phone number ID
   *         twilio_phone: string,          // Phone number (+1234567890)
   *         number_type: string,           // Type: outbound/inbound/sms/voip
   *         status: string,                // Status: assigned/available
   *         channels: array,               // Channels using this number: ['Voice', 'SMS']
   *         a2p_assignments: [             // Optional: if assigned to campaigns
   *           {
   *             campaign_name: string,     // A2P campaign name
   *             channel_type: string       // Voice/SMS/Both
   *           }
   *         ]
   *       }
   *     ],
   *     current_page: number,
   *     last_page: number,
   *     per_page: number,
   *     total: number
   *   }
   * }
   *
   * @param {string} userId - User ID
   * @param {Object} params - Query parameters (search, type, status, per_page, page)
   * @returns {Promise} Phone inventory data with pagination
   */
  getUserPhones: (userId, params = {}) =>
    axios
      .get(`${config.apiBaseURL2}trust-hub/phones/${userId}`, { params })
      .then((resp) => resp.data)
      .catch((err) => {
        console.error('Get User Phones Error:', err);
        return {
          success: false,
          message: err.response?.data?.message || 'Failed to load phone inventory',
          error: err.message
        };
      }),

  /**
   * Assign phone number to Trust Hub Customer Profile
   *
   * Uses Twilio Trust Hub API via backend:
   * POST /v1/CustomerProfiles/{BU}/ChannelEndpointAssignments
   *
   * PREREQUISITES:
   * - Trust Hub Business Profile must be created and approved
   * - Phone must have phone_sid (PN...)
   * - Phone must not already be assigned
   *
   * @param {Object} data - Assignment data
   * @param {string} data.user_id - User ID
   * @param {number} data.phone_id - Phone record ID from zyra_twilio_numbers
   * @param {string} data.assignment_type - 'voice', 'sms', or 'both'
   * @param {number} [data.campaign_id] - Optional A2P campaign ID for SMS
   *
   * @returns {Promise} Assignment result with trust_hub_assignment_sid
   */
  assignPhone: (data) =>
    axios
      .post(`${config.apiBaseURL2}trust-hub/phones/assign`, data)
      .then((resp) => resp.data)
      .catch((err) => {
        console.error('Assign Phone Error:', err);
        return {
          success: false,
          message: err.response?.data?.message || 'Failed to assign phone to Trust Hub profile',
          error: err.message
        };
      }),

  /**
   * Unassign phone number from Trust Hub Customer Profile
   *
   * Uses Twilio Trust Hub API via backend:
   * DELETE /v1/CustomerProfiles/{BU}/ChannelEndpointAssignments/{RA}
   *
   * PREREQUISITES:
   * - Phone must be assigned (have trust_hub_assignment_sid)
   * - User will be warned about affected channels
   *
   * @param {Object} data - Unassignment data
   * @param {string} data.user_id - User ID
   * @param {number} data.phone_id - Phone record ID
   *
   * @returns {Promise} Unassignment result with affected_channels list
   */
  unassignPhone: (data) =>
    axios
      .post(`${config.apiBaseURL2}trust-hub/phones/unassign`, data)
      .then((resp) => resp.data)
      .catch((err) => {
        console.error('Unassign Phone Error:', err);
        return {
          success: false,
          message: err.response?.data?.message || 'Failed to unassign phone from Trust Hub profile',
          error: err.message
        };
      }),

  /**
   * Bulk assign phone numbers to Trust Hub Customer Profile
   *
   * Loops through multiple phones, calls Twilio API for each:
   * POST /v1/CustomerProfiles/{BU}/ChannelEndpointAssignments
   *
   * PREREQUISITES:
   * - Same as single assign, but for multiple phones
   * - Handles partial success/failure
   *
   * @param {Object} data - Bulk assignment data
   * @param {string} data.user_id - User ID
   * @param {number[]} data.phone_ids - Array of phone record IDs
   * @param {string} data.assignment_type - 'voice', 'sms', or 'both'
   * @param {number} [data.campaign_id] - Optional A2P campaign ID for SMS
   *
   * @returns {Promise} Results object with success[] and failed[] arrays
   */
  bulkAssignPhones: (data) =>
    axios
      .post(`${config.apiBaseURL2}trust-hub/phones/bulk-assign`, data)
      .then((resp) => resp.data)
      .catch((err) => {
        console.error('Bulk Assign Phones Error:', err);
        return {
          success: false,
          message: err.response?.data?.message || 'Failed to bulk assign phones to Trust Hub profile',
          error: err.message
        };
      }),

  /**
   * Assign phone number directly to Trust Hub Customer Profile (without campaign)
   *
   * Uses Twilio Trust Hub API via backend:
   * POST /v1/CustomerProfiles/{BU}/ChannelEndpointAssignments
   *
   * PREREQUISITES:
   * - Trust Hub Business Profile must be created and approved
   * - Phone must have phone_number_sid (PN...)
   * - Phone must not already be assigned to this profile
   *
   * @param {Object} data - Assignment data
   * @param {string} data.user_id - User ID
   * @param {string} data.profile_sid - Trust Hub Customer Profile SID (BU...)
   * @param {string} data.phone_number_sid - Phone Number SID (PN...)
   *
   * @returns {Promise} Assignment result with assignment_sid (RA...)
   */
  assignPhoneProfile: (data) =>
    axios
      .post(`${config.apiBaseURL2}trust-hub/profile/assign-phone`, data)
      .then((resp) => resp.data)
      .catch((err) => {
        console.error('Assign Phone to Profile Error:', err);
        return {
          success: false,
          message: err.response?.data?.message || 'Failed to assign phone to Trust Hub profile',
          error: err.message
        };
      }),

  /**
   * Bulk assign phone numbers directly to Trust Hub Customer Profile (without campaign)
   *
   * Loops through multiple phones, calls Twilio API for each:
   * POST /v1/CustomerProfiles/{BU}/ChannelEndpointAssignments
   *
   * PREREQUISITES:
   * - Trust Hub Business Profile must be created and approved
   * - All phones must have phone_number_sids (PN...)
   * - Handles partial success/failure
   *
   * @param {Object} data - Bulk assignment data
   * @param {string} data.user_id - User ID
   * @param {string} data.profile_sid - Trust Hub Customer Profile SID (BU...)
   * @param {string[]} data.phone_number_sids - Array of Phone Number SIDs (PN...)
   *
   * @returns {Promise} Results object with success_count, failed_count, success[], failed[]
   */
  bulkAssignPhonesProfile: (data) =>
    axios
      .post(`${config.apiBaseURL2}trust-hub/profile/bulk-assign-phones`, data)
      .then((resp) => resp.data)
      .catch((err) => {
        console.error('Bulk Assign Phones to Profile Error:', err);
        return {
          success: false,
          message: err.response?.data?.message || 'Failed to bulk assign phones to Trust Hub profile',
          error: err.message
        };
      }),

  // ============================================
  // A2P Campaign APIs
  // ============================================

  /**
   * Get A2P campaigns for user
   * @param {string} userId - User ID
   * @returns {Promise} A2P campaigns list
   */
  getA2PCampaigns: (userId) =>
    axios
      .get(`${config.apiBaseURL2}a2p/campaigns/${userId}`)
      .then((resp) => resp.data)
      .catch((err) => {
        console.error('Get A2P Campaigns Error:', err);
        return {
          success: false,
          message: err.response?.data?.message || 'Failed to load campaigns',
          error: err.message,
          data: []
        };
      }),

  // ============================================
  // Verified Caller ID APIs
  // ============================================

  /**
   * Add a verified caller ID
   * @param {Object} data - { user_id, phone_number }
   * @returns {Promise} Added caller ID data
   */
  addVerifiedCallerId: (data) =>
    axios
      .post(`${config.apiBaseURL2}verified-caller-id/add`, data)
      .then((resp) => resp.data)
      .catch((err) => {
        console.error('Add Verified Caller ID Error:', err);
        return {
          success: false,
          message: err.response?.data?.message || 'Failed to add verified caller ID',
          error: err.message
        };
      }),

  /**
   * Get verified caller IDs for a user
   * @param {string} userId - User ID
   * @returns {Promise} List of verified caller IDs
   */
  getVerifiedCallerIds: (userId) =>
    axios
      .get(`${config.apiBaseURL2}verified-caller-id/list/${userId}`)
      .then((resp) => resp.data)
      .catch((err) => {
        console.error('Get Verified Caller IDs Error:', err);
        return {
          success: false,
          message: err.response?.data?.message || 'Failed to load verified caller IDs',
          error: err.message,
          data: []
        };
      }),

  /**
   * Delete a verified caller ID
   * @param {string} id - Caller ID record ID
   * @returns {Promise} Deletion result
   */
  deleteVerifiedCallerId: (id) =>
    axios
      .delete(`${config.apiBaseURL2}verified-caller-id/delete/${id}`)
      .then((resp) => resp.data)
      .catch((err) => {
        console.error('Delete Verified Caller ID Error:', err);
        return {
          success: false,
          message: err.response?.data?.message || 'Failed to delete verified caller ID',
          error: err.message
        };
      }),

  /**
   * Verify OTP for verified caller ID (SMS method)
   * @param {Object} data - { user_id, caller_id_id, otp }
   * @returns {Promise} Verification result
   */
  verifyVerifiedCallerIdOtp: (data) =>
    axios
      .post(`${config.apiBaseURL2}verified-caller-id/verify`, data)
      .then((resp) => resp.data)
      .catch((err) => {
        console.error('Verify OTP Error:', err);
        return {
          success: false,
          message: err.response?.data?.message || 'Failed to verify OTP',
          error: err.message
        };
      }),

  // ============================================
  // Utility APIs
  // ============================================

  /**
   * Get US states list
   * @returns {Promise} US states data
   */
  getUsStates: () =>
    axios
      .get(`${config.apiBaseURL2}states`)
      .then((resp) => resp.data)
      .catch((err) => {
        console.error('Get US States Error:', err);
        return {
          success: false,
          message: err.response?.data?.message || 'Failed to load states',
          error: err.message
        };
      }),
};

export default trustHubCentralized;
